import React, { useEffect, useMemo, useState } from 'react';
import { netlabApi } from './api';
import { iconUrl, kindLabel, stepFor } from './assets';
import { ProfileStats, Sparkline } from './ProfileSection';

// Fields worth seeing without expanding "all parameters".
const PRIMARY = {
  bus: ['name', 'vn_kv', 'in_service'],
  line: ['name', 'std_type', 'length_km', 'parallel', 'in_service'],
  trafo: ['name', 'std_type', 'sn_mva', 'vn_hv_kv', 'vn_lv_kv', 'tap_pos', 'in_service'],
  switch: ['name', 'closed', 'type'],
  load: ['name', 'p_mw', 'q_mvar', 'scaling', 'in_service'],
  sgen: ['name', 'p_mw', 'q_mvar', 'scaling', 'in_service'],
  gen: ['name', 'p_mw', 'vm_pu', 'in_service'],
  storage: ['name', 'max_p_mw', 'max_e_mwh', 'soc_percent', 'p_mw', 'in_service'],
  ext_grid: ['name', 'vm_pu', 'va_degree', 'in_service'],
};

const ADDABLE_ON_BUS = ['load', 'pv', 'wind', 'ev_charger', 'heat_pump', 'battery', 'ext_grid', 'trafo'];

function FieldInput({ field, value, onChange }) {
  if (field.readonly) return <input value={value ?? ''} disabled />;
  if (field.type === 'bool') {
    return (
      <label className="nl-toggle">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <span />
      </label>
    );
  }
  if (field.type === 'select') {
    return (
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {value != null && !field.options.includes(value) && <option value={value}>{value}</option>}
        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <input type={field.type === 'number' ? 'number' : 'text'} step={stepFor(field.key)}
      value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
  );
}

function AssetCard({ sid, item, version, view, onSelectRef, onRemove, onModify, onStartAdd, onZoom, onDeselect, busy }) {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState('');
  const [edits, setEdits] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [addKind, setAddKind] = useState('');

  useEffect(() => {
    let cancelled = false;
    netlabApi.element(sid, item.table, item.id)
      .then(d => { if (!cancelled) { setDetails(d.element); setError(''); } })
      .catch(e => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [sid, item.table, item.id, version]);

  useEffect(() => { setEdits({}); }, [version]);

  const obj = view.byKey.get(item.key);
  const kind = details?.kind || obj?.kind || item.table;
  const primaryKeys = PRIMARY[item.table] || ['name', 'in_service'];
  const fields = details?.fields || [];
  const shown = showAll ? fields.filter(f => !f.readonly) : fields.filter(f => primaryKeys.includes(f.key));
  const dirty = Object.keys(edits).length > 0;
  const valueOf = (f) => (f.key in edits ? edits[f.key] : f.value);
  const inService = fields.find(f => f.key === 'in_service');

  const cascade = item.table === 'bus' && details?.attached?.length
    ? ` and ${details.attached.length} connected element${details.attached.length > 1 ? 's' : ''}` : '';

  return (
    <div className={`nl-card${collapsed ? ' collapsed' : ''}`}>
      <div className="nl-card-head" onClick={() => setCollapsed(c => !c)}>
        <img src={iconUrl(kind, kind === 'switch' && obj && !obj.closed ? 'open' : undefined)} alt="" className="nl-card-icon" />
        <div className="nl-card-title">
          <div className="nl-card-name" title={details?.name || obj?.name}>{details?.name || obj?.name || '…'}</div>
          <div className="nl-card-kind">
            {kindLabel(kind)} · #{item.id}
            {obj?.vn_kv != null && ` · ${obj.vn_kv} kV`}
            {obj && obj.in_service === false && <span className="nl-badge off">out of service</span>}
          </div>
        </div>
        <button className="nl-icon-btn" title="Zoom to" onClick={(e) => { e.stopPropagation(); onZoom(obj); }}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round"><circle cx="11" cy="11" r="6" /><path d="m20 20-4.2-4.2" /></svg>
        </button>
        <button className="nl-icon-btn" title="Remove from selection"
          onClick={(e) => { e.stopPropagation(); onDeselect(item); }}>×</button>
      </div>

      {!collapsed && (
        <>
          <div className="nl-card-body">
            {error && <div className="nl-error">{error}</div>}
            {!details && !error && <div className="nl-muted">Loading…</div>}

            {details && Object.keys(details.refs || {}).length > 0 && (
              <div className="nl-refs">
                {Object.entries(details.refs).map(([k, r]) => (
                  <button key={k} className="nl-chip" onClick={() => onSelectRef({ table: 'bus', id: r.id })}>
                    <span className="nl-chip-k">{k.replace('_', ' ')}</span> {r.name}
                  </button>
                ))}
              </div>
            )}

            {details && (
              <div className="nl-fields">
                {shown.map(f => (
                  <label key={f.key} className={`nl-field row${f.key in edits ? ' edited' : ''}`}>
                    <span>{f.key}</span>
                    <FieldInput field={f} value={valueOf(f)}
                      onChange={(v) => setEdits(e => {
                        const next = { ...e };
                        if (v === f.value) delete next[f.key]; else next[f.key] = v;
                        return next;
                      })} />
                  </label>
                ))}
              </div>
            )}
            {details && (
              <button className="nl-link" onClick={() => setShowAll(s => !s)}>
                {showAll ? 'Show main parameters' : `All parameters (${fields.filter(f => !f.readonly).length})`}
              </button>
            )}

            {details?.profile && (
              <div className="nl-profile nl-card-profile">
                <div className="nl-subtitle">Profile · {details.profile.category}</div>
                <Sparkline summary={details.profile}
                  color={details.profile.category === 'generation' ? '#facc15' : '#f0abfc'} />
                <ProfileStats summary={details.profile} />
                {details.profile.source && <small className="nl-muted">{details.profile.source}</small>}
              </div>
            )}

            {details?.attached?.length > 0 && (
              <div className="nl-attached">
                <div className="nl-subtitle">Connected</div>
                <div className="nl-refs">
                  {details.attached.map(a => (
                    <button key={`${a.table}:${a.id}`} className="nl-chip" onClick={() => onSelectRef(a)}>
                      <img src={iconUrl(a.kind)} alt="" /> {a.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="nl-card-actions wrap">
            {dirty ? (
              <>
                <button className="nl-btn ghost small" onClick={() => setEdits({})}>Reset</button>
                <button className="nl-btn primary small" disabled={busy}
                  onClick={() => onModify(item, edits)}>Save changes</button>
              </>
            ) : confirming ? (
              <>
                <span className="nl-confirm">Remove {details?.name || 'this'}{cascade}?</span>
                <button className="nl-btn ghost small" onClick={() => setConfirming(false)}>Keep</button>
                <button className="nl-btn danger small" disabled={busy} onClick={() => onRemove([item])}>Remove</button>
              </>
            ) : (
              <>
                <button className="nl-btn danger-ghost small" onClick={() => setConfirming(true)}>Remove</button>
                {inService && (
                  <button className="nl-btn ghost small" disabled={busy}
                    onClick={() => onModify(item, { in_service: !inService.value })}>
                    {inService.value ? 'Take out of service' : 'Put in service'}
                  </button>
                )}
                {item.table === 'switch' && obj && (
                  <button className="nl-btn ghost small" disabled={busy}
                    onClick={() => onModify(item, { closed: !obj.closed })}>
                    {obj.closed ? 'Open switch' : 'Close switch'}
                  </button>
                )}
                {item.table === 'bus' && (
                  <>
                    <select className="nl-add-select" value={addKind}
                      onChange={(e) => { const k = e.target.value; setAddKind(''); if (k) onStartAdd({ kind: k, bus: item.id, hv_bus: k === 'trafo' ? item.id : undefined }); }}>
                      <option value="">Add ▾</option>
                      {ADDABLE_ON_BUS.map(k => <option key={k} value={k}>{kindLabel(k)}</option>)}
                    </select>
                    <button className="nl-btn ghost small" onClick={() => onStartAdd({ kind: 'line', from_bus: item.id, pickTo: true })}>
                      Connect to bus…
                    </button>
                  </>
                )}
                {item.table === 'line' && obj && (
                  <button className="nl-btn ghost small"
                    onClick={() => onStartAdd({ kind: 'switch', element: item.id, bus: obj.from_bus })}>
                    Add switch
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function SelectionPanel({ sid, selection, version, view, busy, onClear, onDeselect, onSelectRef,
  onRemove, onModify, onStartAdd, onZoom }) {
  const [confirmAll, setConfirmAll] = useState(false);
  useEffect(() => { setConfirmAll(false); }, [selection.length]);

  const items = useMemo(() => [...selection].reverse(), [selection]);  // newest first

  if (!selection.length) return null;
  return (
    <div className="nl-selection">
      <div className="nl-selection-head">
        <span>Selected <b>{selection.length}</b></span>
        <div className="nl-selection-actions">
          {confirmAll ? (
            <>
              <button className="nl-btn ghost small" onClick={() => setConfirmAll(false)}>Keep</button>
              <button className="nl-btn danger small" disabled={busy} onClick={() => onRemove(selection)}>
                Remove {selection.length}
              </button>
            </>
          ) : (
            <>
              {selection.length > 1 && (
                <button className="nl-btn danger-ghost small" onClick={() => setConfirmAll(true)}>Remove all</button>
              )}
              <button className="nl-btn ghost small" onClick={onClear}>Clear</button>
            </>
          )}
        </div>
      </div>
      {items.map(item => (
        <AssetCard key={item.key} sid={sid} item={item} version={version} view={view} busy={busy}
          onSelectRef={onSelectRef} onRemove={onRemove} onModify={onModify} onStartAdd={onStartAdd}
          onZoom={onZoom} onDeselect={onDeselect} />
      ))}
    </div>
  );
}
